import {
    AddAttachmentDto,
    AddCustomerImpactDto,
    AddWatcherDto,
    AssignRequestDto,
    AttachmentDto,
    CreateCssReviewRequestDto,
    CssApiClient,
    CssRequestDto,
    CustomerImpactDto,
    EditCssReviewRequestDto,
    EditRequestDto,
    EntityType,
    HashTagDto,
    IAddAttachmentDto,
    IAddCustomerImpactDto,
    IAssignRequestDto,
    ICreateCssReviewRequestDto,
    ICssRequestDto,
    ICustomerImpactDto,
    IHashTagDto,
    IUpdateClassificationDto,
    RequestType,
    UpdateClassificationDto,
    UserDto
} from "generated/css-backend";
import { IGetAttachment, IShareRequest } from "app/models/common/request";
import { API, appInsightsService, RouteConstants } from "app/utils/constants";
import { useMutation, useQuery } from "react-query";
import { AppQueryClient } from "./clients/query-client";
import { RequestDto } from "generated/request-backend";

// The local environment has a trailing slash in the API_SERVER_URL, but test and prod do not.
const baseUrl : string = API.API_SERVER_URL.endsWith('/') ? API.API_SERVER_URL.slice(0,-1) : API.API_SERVER_URL;

//#region Queries
export const useGetAttachments = (requestData: IGetAttachment) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);
    return useQuery(
        ['useGetAttachments'],
        async () => {
            if (requestData) {
                return await cssApi.reviewRequest_GetAttachments(requestData.id, requestData.entityType, requestData.portal);
            }
        },
        { cacheTime: 0 }
    );
};
//#endregion
//#region Mutations
export const useShareRequest = (data: IShareRequest) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);
    const users : UserDto[] = [];
    data.users.map(user => users.push(new UserDto({...user})));

    const attachments : AttachmentDto[] = [];
    data.attachments.map(attachment => attachments.push(new AttachmentDto({...attachment})));

    switch (data.requestType) {
        case RequestType.CSSRequest:
            // Original function was calling css/cssrequest/watcher
            // new backend doesn't have css/cssrequest/watcher, but does have css/ReviewRequest/Watcher (The only result I found when searching /watcher)
            return cssApi.reviewRequest_UpdateWatcher(new AddWatcherDto({...data, users: users, attachments: attachments}));
        case RequestType.CssReviewRequest:
            return cssApi.reviewRequest_UpdateWatcher(new AddWatcherDto({...data, users: users, attachments: attachments}));
        default:
            break;
    }
};

/**
 * Mutation used to submit updated request from unified dashboard
 */
export const useUpdateRequest = (data: RequestDto) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);
    const dataInterface = {
        ...data,
        // requestedResolutionDate: new Date(data.requestedResolutionDate),
        // companyInformation: new CompanyInformationDto({ ...data.companyInformation })
    }

    switch (data.requestType) {
        case RequestType.CSSRequest:
            return cssApi.cssRequest_UpdateCss(new EditRequestDto(dataInterface));
        case RequestType.CssReviewRequest:
            let reviewDto = new EditCssReviewRequestDto(dataInterface);
            reviewDto.init(dataInterface);
            return cssApi.reviewRequest_UpdateCssReviewRequest(reviewDto);
            // return cssApi.reviewRequest_UpdateCssReviewRequest(new EditCssReviewRequestDto(dataInterface));
        default:
            // Error
            break;
    }
};

/**
 * Module: Admin Assign Request
 * Custom hook to assign a user to a request */
export const useAssignRequest = (requestType: RequestType, data: IAssignRequestDto) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);
    const assignDto = new AssignRequestDto(data);

    switch (requestType) {
        case RequestType.CSSRequest:
            return cssApi.cssRequest_Assign(assignDto);
        case RequestType.CssReviewRequest:
            return cssApi.reviewRequest_Assign(assignDto);
        default:
            // Error
            break;
    }
};

/**
 * Module: Create Request
 * Used for creating new requests (e.g., aggregate parent creation) */
export const useCreateRequest = (requestType: RequestType, data: ICssRequestDto | ICreateCssReviewRequestDto) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);

    switch (requestType) {
        case RequestType.CSSRequest:
            const requestDto : CssRequestDto = new CssRequestDto();
            requestDto.init(data as ICssRequestDto);
            return cssApi.cssRequest_CreateCss(requestDto);
        case RequestType.CssReviewRequest:
            const reviewRequestDto : CreateCssReviewRequestDto = new CreateCssReviewRequestDto();
            reviewRequestDto.init(data as ICreateCssReviewRequestDto);
            return cssApi.reviewRequest_CreateCssReviewRequest(reviewRequestDto);
        default:
            // Error
            break;
    }
};

export const usePostAddCustomer = (route: RouteConstants, data: IAddCustomerImpactDto) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);

    switch (route) {
        case RouteConstants.CSS_CSS_REQUEST_ADDCUSTOMERIMPACT:
        case RouteConstants.CSS_REVIEW_REQUEST_ADDCUSTOMERIMPACT:
            return cssApi.reviewRequest_AddCustomerImpact(new AddCustomerImpactDto(data));
        default:
            // Error
            break;
    }
};

export const useAddAttachment = (data: IAddAttachmentDto) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);
    // css only has attachments on review request url in css backend apparently? None on css/cssrequest
    return cssApi.reviewRequest_AddAttachment(new AddAttachmentDto(data));
};

export const useRemoveAttachment = (id:string, entityId?:string, entityType?:EntityType) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);
    // css only has attachments on review request url in css backend apparently? None on css/cssrequest
    return cssApi.reviewRequest_DeleteAttachmentById(id, entityId, entityType);
};

export const useUpdateAttachmentClassification = (data: IUpdateClassificationDto) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);
    // css only has attachments on review request url in css backend apparently? None on css/cssrequest
    return cssApi.reviewRequest_UpdateClassification(new UpdateClassificationDto(data));
};

export const usePostAddCustomerReviewRequest = () => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);

    const requestTypeMutation = useMutation((customerImpact: IAddCustomerImpactDto) => cssApi.reviewRequest_AddCustomerImpact(new AddCustomerImpactDto(customerImpact)), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
            }, 2000);
            AppQueryClient.invalidateQueries('getRequestById');
            AppQueryClient.invalidateQueries('requestQuery');
            AppQueryClient.invalidateQueries('listBuilder');
            appInsightsService.logEvent('usePostAddCustomerReviewRequest was Succesful');
        }
    });
    return requestTypeMutation;
};

export const useRemoveCustomerImpact = (route: RouteConstants, id: string, data: ICustomerImpactDto) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);

    switch (route) {
        case RouteConstants.CSS_CSS_REQUEST:
            return cssApi.cssRequest_DeleteCustomerImpact(id, new CustomerImpactDto(data));
        case RouteConstants.CSS_REVIEW_REQUEST:
            return cssApi.reviewRequest_DeleteCustomerImpact(id, new CustomerImpactDto(data));
        default:
            // Error
            break;
    }
};

export const usePostCommentsHashTags = (id: string, data: IHashTagDto[]) => {
    const cssApi : CssApiClient = new CssApiClient(baseUrl);
    const hashTags : HashTagDto[] = data.map(hashTag => {
        return new HashTagDto(hashTag)
    });
    // Only Review Requests have handling for adding hash tags
    return cssApi.reviewRequest_AddHashTags(id, hashTags);
};
//#endregion